import React, { useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import { OrgChartRow, getDirectReports, isManagerEmail } from '../../../support/orgScope';
import { useFetchDashboardPlacements, useFetchDashboardTileLinks } from '../../../hooks/toolbox-hooks';
import type { DashboardPlacement, DashboardTileLink } from '../../../types/dashboard';
import type { TileRuntimeContext } from '../../../support/tileRuntimeContext';
import LeaderTileStrip from '../LeaderTileStrip';

/**
 * Leader > Firestarter. The Firestarter (Jira SBE request) tiles, configured in the database as the
 * `dashboard` placement of this component key. Team and individual views share this page: the tiles are
 * not yet scoped to a person or team, so both modes show the same tiles.
 */
export const FIRESTARTER_COMPONENT_KEY = 'Leader.FirestarterTeamSummaryPage';
export const FIRESTARTER_REGION_KEY = 'dashboard';

interface Props {
  orgRows: OrgChartRow[];
  email: string;
  enableDebugLogging?: boolean;
}

const layoutOptions = (placement: any) =>
  placement?.layout?.options ?? placement?.placement_layout?.options ?? placement?.options ?? {};

export default function FirestarterPage({ orgRows, email, enableDebugLogging = false }: Props) {
  const normalizedEmail = String(email || '').trim().toLowerCase();

  const { data: placements, isLoading: loadingPlacements, error: placementsError } = useFetchDashboardPlacements(
    { component_key: FIRESTARTER_COMPONENT_KEY, region_key: FIRESTARTER_REGION_KEY, enabled: true },
    { placeholderData: keepPreviousData, refetchOnWindowFocus: false },
  );

  const placement: DashboardPlacement | null = useMemo(() => {
    const arr = Array.isArray(placements) ? placements : [];
    return arr.length ? arr[0] : null;
  }, [placements]);

  const { data: tileLinks, isLoading: loadingLinks, error: linksError } = useFetchDashboardTileLinks(
    placement?.id ? { placement: placement.id, enabled: true, order_by: 'order' } : undefined,
    { enabled: !!placement?.id, placeholderData: keepPreviousData, refetchOnWindowFocus: false },
  );

  const runtimeContext: TileRuntimeContext = useMemo(() => {
    const isTeam = isManagerEmail(orgRows, normalizedEmail);
    const teamEmails = isTeam
      ? getDirectReports(orgRows, normalizedEmail).map((r) => String(r.user_email || '').toLowerCase())
      : [];
    return {
      email: normalizedEmail,
      managerEmail: normalizedEmail,
      domain: 'salesforce' as const,
      teamScope: teamEmails,
      teamEmailsList: teamEmails.join(','),
      teamSize: teamEmails.length,
      viewMode: isTeam ? ('team' as const) : ('individual' as const),
      isTeamView: isTeam,
      isIndividualView: !isTeam,
    };
  }, [orgRows, normalizedEmail]);

  if (placementsError || linksError) {
    return (
      <div style={{ padding: 12, color: 'red' }}>{((placementsError || linksError) as Error).message}</div>
    );
  }
  if (loadingPlacements || loadingLinks) return <HbrSpinner>Loading dashboard…</HbrSpinner>;

  const links = (Array.isArray(tileLinks) ? (tileLinks as DashboardTileLink[]) : [])
    .filter((l) => !!l?.tile?.name)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id);

  if (!placement || links.length === 0) {
    return <div className="hbr-css__text-secondary-sm">No Firestarter tiles are configured.</div>;
  }

  const opts = layoutOptions(placement);
  const columns = Math.max(1, Number(opts?.grid?.columns ?? 1));
  const height = Number(opts?.container?.height ?? 350);
  const padding = Number(opts?.container?.padding ?? 12);
  const gap = Number(opts?.grid?.gap ?? opts?.container?.gap ?? 12);

  return (
    <div className="flex flex-col gap-4 h-full min-h-0 min-w-0">
      <div className="hbr-css__container-card w-full" style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <div className="hbr-css__text-primary-sm-semibold px-3 pt-3">{placement.label || 'Firestarter'}</div>
        <div style={{ height, minHeight: 0, minWidth: 0, padding, overflow: 'hidden' }}>
          <div
            className="grid"
            style={{
              height: '100%',
              minHeight: 0,
              minWidth: 0,
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              gap,
            }}
          >
            {links.map((link) => {
              const colSpan = Number((link.options as any)?.colSpan ?? 1);
              const minHeight = Number((link.options as any)?.minHeight ?? 0);
              return (
                <div
                  key={`firestarter-tile-${link.id}`}
                  className="min-w-0 flex flex-col"
                  style={{
                    minHeight,
                    height: '100%',
                    minWidth: 0,
                    gridColumn: `span ${Math.max(1, Math.min(columns, colSpan))}`,
                  }}
                >
                  <LeaderTileStrip
                    tileNames={[link.tile!.name]}
                    runtimeContext={runtimeContext}
                    className="h-full min-w-0"
                    enableDebugLogging={enableDebugLogging}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
